import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import useSteps from '../../hooks/useSteps';
import Big from 'big.js';
import { EarningWithholdingConceptDTO } from '../../interface/interface';

type Taxes = {
    iibb: Big,
    earnings: Big,
    others: Big,
}
type PaidTax = {
    taxtype: number;
    amount: Big;
    percentage: Big;
};
type PaymentOrderContextType = {
    supplier: any,
    setSupplier: Dispatch<SetStateAction<any>>,
    sinceDate: Date | null,
    setSinceDate: Dispatch<SetStateAction<Date|null>>,
    untilDate: Date | null,
    setUntilDate: Dispatch<SetStateAction<Date|null>>,
    steps: any,
    stepMovement: {
        stepIndex: number,
        nextStep: () => void,
        prevStep: () => void,
    },
    invoices: any,
    setInvoices: Dispatch<SetStateAction<any>>,
    paymentArray: PaymentArray[],
    setPaymentArray: Dispatch<SetStateAction<PaymentArray[]>>,
    taxes: Taxes,
    setTaxes: Dispatch<SetStateAction<Taxes>>,
    totalWithoutTaxes: Big,
    totalToPay: Big,
    setTotalToPay: Dispatch<SetStateAction<Big>>, // <-- Añadido
    earningWithHoldingConcept: EarningWithholdingConceptDTO,
    setEarningWithHoldingConcept: Dispatch<SetStateAction<EarningWithholdingConceptDTO>>
    paidTaxes: PaidTax[]; // Nueva propiedad
    addPaidTax: (taxtype: number, amount: Big, percentage: Big) => void; // Función para agregar
    removePaidTax: (taxtype: number) => void; // Función para eliminar
    cleanAll: () => void,
}


const PaymentOrderContext = createContext<PaymentOrderContextType>({
    supplier: null,
    setSupplier: () => { },
    sinceDate: null,
    setSinceDate: () => { },
    untilDate: null,
    setUntilDate: () => { },
    steps: null,
    stepMovement: {
        stepIndex: 0,
        nextStep: () => { },
        prevStep: () => { },
    },
    invoices: [],
    setInvoices: () => { },
    paymentArray: [],
    setPaymentArray: () => { },
    taxes: {
        iibb: Big(0),
        earnings: Big(0),
        others: Big(0),
    },
    setTaxes: () => { },
    totalWithoutTaxes: Big(0),
    setTotalToPay: () => { },
    totalToPay: Big(0),
    earningWithHoldingConcept: {withholdingconcept:0, description:'-'},
    setEarningWithHoldingConcept: () => { },
    paidTaxes: [],
    addPaidTax: () => { },
    removePaidTax: () => { }, 
    cleanAll: () => { },
});

export type OrderId = `${string}|${string}`

export interface PaymentArray {
    orderId: OrderId
    paymentValue: number
    paymentToPay: number,
}

export default function PaymentOrderProvider(props: any) {

    const { steps, children } = props
    const [supplier, setSupplier] = useState(null)
    const [sinceDate, setSinceDate] = useState<Date|null>(null)
    const [untilDate, setUntilDate] = useState<Date|null>(null)
    const { stepIndex, nextStep, prevStep, resetSteps } = useSteps(steps)
    const [invoices, setInvoices] = useState([])
    const [totalWithoutTaxes, setTotalWithoutTaxes] = useState(Big(0))
    const [totalToPay, setTotalToPay] = useState(Big(0))
    const [earningWithHoldingConcept, setEarningWithHoldingConcept] = useState<EarningWithholdingConceptDTO>()
    const [paymentArray, setPaymentArray] = useState<PaymentArray[]>([])
    const [taxes, setTaxes] = useState<Taxes>({
        iibb: Big(0),
        earnings: Big(0),
        others: Big(0),
    })

    const [paidTaxes, setPaidTaxes] = useState<PaidTax[]>([]); // Nuevo estado
    // Agregar un impuesto pagado
    const addPaidTax = (taxtype: number, amount: Big, percentage: Big) => {
        setPaidTaxes((prev) => [...prev, { taxtype, amount, percentage }]);
        setTotalToPay(((prev) => prev.minus(Big(amount.toFixed(2))))); // Actualizar el total
    };

    // Eliminar un impuesto pagado
    const removePaidTax = (taxtype: number) => {
        setPaidTaxes((prev) => {
            const taxToRemove = prev.find((tax) => tax.taxtype === taxtype);
            if (taxToRemove) {
                setTotalToPay((prevTotal) => prevTotal.plus(Big(taxToRemove.amount.toFixed(2))));
                return prev.filter((tax) => tax.taxtype !== taxtype);
            }
            return prev;
        });
    };

    function cleanAll() {
        setSupplier(null)
        setSinceDate(null)
        setUntilDate(null)
        setInvoices([])
        setPaymentArray([])
        setTaxes({
            iibb: Big(0),
            earnings: Big(0),
            others: Big(0),
        })
        resetSteps()
        setTotalToPay(Big(0))
        setTotalWithoutTaxes(Big(0))
    }

    useEffect(() => {
        setTotalWithoutTaxes(paymentArray.reduce((acc, curr) => acc.plus(curr.paymentToPay), Big(0)))
    }, [paymentArray])

    useEffect(() => {
        setTotalToPay(totalWithoutTaxes.minus(taxes.iibb).minus(taxes.earnings).minus(taxes.others))
    }, [taxes, totalWithoutTaxes])

    useEffect(() => {
        if (supplier) {
            setInvoices([])
            setPaymentArray([])
        }
    }, [supplier])

    return (
        <PaymentOrderContext.Provider value={{
            supplier,
            setSupplier,
            sinceDate,
            setSinceDate,
            untilDate,
            setUntilDate,
            stepMovement: { stepIndex, nextStep, prevStep },
            invoices,
            setInvoices,
            steps,
            paymentArray,
            setPaymentArray,
            taxes,
            setTaxes,
            totalWithoutTaxes,
            totalToPay,
            setTotalToPay, // <-- Incluido aquí
            earningWithHoldingConcept,
            setEarningWithHoldingConcept,
            paidTaxes,
            addPaidTax,
            removePaidTax,
            cleanAll
        }}>
            {children}
        </PaymentOrderContext.Provider>
    )    
}

export const usePaymentOrder = () => useContext(PaymentOrderContext)